import React from 'react';
import './Phase2.css';
import { POINT_SYSTEM, KNOCKOUT_MATCHES } from '../../constants';
import MatchComponent from '../MatchComponent/MatchComponent';
import RoundHeader from '../RoundHeader/RoundHeader';
import Tabs from '../../../../components/Tabs/Tabs';
import TabPanel from '../../../../components/TabPanel/TabPanel';
import SaveGuessSection, { SaveStatus } from './SaveGuessSection';
import PointSummary from '../PointSummary/PointSummary';

const Phase2 = ({ 
  playoffPredictions, 
  knockoutPredictions,
  savedPlayoffPredictions = {},
  savedKnockoutPredictions = {},
  onPlayoffPredictionChange, 
  onKnockoutPredictionChange, 
  phaseLocks = {}, 
  realResults = {},
  savedGuesses = {},
  onSaveGroupGuess
}) => {
  // Hent kampene fra databasen (realResults) eller brug fallback
  const getPlayoffMatchups = () => {
    if (realResults?.playoffResults && Array.isArray(realResults.playoffResults) && realResults.playoffResults.length > 0) {
      return realResults.playoffResults
        .filter(match => match && match.home && match.away)
        .map((match, index) => ({
          home: match.home,
          away: match.away,
          id: match.id || `playoff_${index}`
        }));
    }
    // Fallback hvis ingen kampene er sat op endnu
    return Array.from({ length: 8 }, (_, i) => ({
      home: 'TBD',
      away: 'TBD',
      id: `playoff_${i}`
    }));
  };

  const getEighthFinalMatchups = () => {
    if (realResults?.roundOf16Results && Array.isArray(realResults.roundOf16Results) && realResults.roundOf16Results.length > 0) {
      return realResults.roundOf16Results
        .filter(match => match && match.home && match.away)
        .map((match, index) => ({
          home: match.home,
          away: match.away,
          id: match.id || `eighth_${index}`
        }));
    }
    // Fallback hvis ingen kampene er sat op endnu
    return Array.from({ length: 8 }, (_, i) => ({
      home: 'TBD',
      away: 'TBD',
      id: `eighth_${i}`
    }));
  };

  const getQuarterFinalMatchups = () => {
    if (realResults?.quarterFinalResults && Array.isArray(realResults.quarterFinalResults) && realResults.quarterFinalResults.length > 0) {
      return realResults.quarterFinalResults
        .filter(match => match && match.home && match.away)
        .map((match, index) => ({
          home: match.home,
          away: match.away,
          id: match.id || `quarter_${index}`
        }));
    }
    // Fallback hvis ingen kampene er sat op endnu
    return Array.from({ length: 4 }, (_, i) => ({
      home: 'TBD',
      away: 'TBD',
      id: `quarter_${i}`
    }));
  };

  const getSemiFinalMatchups = () => {
    if (realResults?.semiFinalResults && Array.isArray(realResults.semiFinalResults) && realResults.semiFinalResults.length > 0) {
      return realResults.semiFinalResults
        .filter(match => match && match.home && match.away)
        .map((match, index) => ({
          home: match.home,
          away: match.away,
          id: match.id || `semi_${index}`
        }));
    }
    // Fallback hvis ingen kampene er sat op endnu
    return Array.from({ length: 2 }, (_, i) => ({
      home: 'TBD',
      away: 'TBD',
      id: `semi_${i}`
    }));
  };

  const playoffMatchups = getPlayoffMatchups();
  const eighthFinalMatchups = getEighthFinalMatchups();
  const quarterFinalMatchups = getQuarterFinalMatchups();
  const semiFinalMatchups = getSemiFinalMatchups();

  // Bestem hvilke tabs der skal være disabled baseret på phase locks
  const isPlayoffLocked = phaseLocks.phase2Playoff || false;
  const isRound16Locked = phaseLocks.phase2Round16 || false;
  const isQuartersLocked = phaseLocks.phase2Quarters || false;
  const isSemisLocked = phaseLocks.phase2Semis || false;

  // Tjek om alle kampe i en gruppe er gættet på
  // Brug KNOCKOUT_MATCHES konstanten til at validere antallet
  const areAllPlayoffMatchesGuessed = () => {
    const validPredictions = Object.values(playoffPredictions).filter(
      val => val && val !== ''
    );
    return validPredictions.length === KNOCKOUT_MATCHES.PLAYOFF;
  };

  const areAllRound16MatchesGuessed = () => {
    if (!knockoutPredictions.eighthFinals) return false;
    const validPredictions = Object.values(knockoutPredictions.eighthFinals).filter(
      val => val && val !== ''
    );
    return validPredictions.length === KNOCKOUT_MATCHES.EIGHTH_FINALS;
  };

  const areAllQuarterMatchesGuessed = () => {
    if (!knockoutPredictions.quarterFinals) return false;
    const validPredictions = Object.values(knockoutPredictions.quarterFinals).filter(
      val => val && val !== ''
    );
    return validPredictions.length === KNOCKOUT_MATCHES.QUARTER_FINALS;
  };

  const areAllSemiMatchesGuessed = () => {
    if (!knockoutPredictions.semiFinals) return false;
    const validPredictions = Object.values(knockoutPredictions.semiFinals).filter(
      val => val && val !== ''
    );
    return validPredictions.length === KNOCKOUT_MATCHES.SEMI_FINALS;
  };

  // Tæl hvor mange kampe der er gættet på i hver gruppe
  // Brug faktiske predictions i stedet for at matche mod matchups
  const getPlayoffGuessedCount = () => {
    return Object.values(playoffPredictions).filter(
      val => val && val !== ''
    ).length;
  };

  const getRound16GuessedCount = () => {
    if (!knockoutPredictions.eighthFinals) return 0;
    return Object.values(knockoutPredictions.eighthFinals).filter(
      val => val && val !== ''
    ).length;
  };

  const getQuarterGuessedCount = () => {
    if (!knockoutPredictions.quarterFinals) return 0;
    return Object.values(knockoutPredictions.quarterFinals).filter(
      val => val && val !== ''
    ).length;
  };

  const getSemiGuessedCount = () => {
    if (!knockoutPredictions.semiFinals) return 0;
    return Object.values(knockoutPredictions.semiFinals).filter(
      val => val && val !== ''
    ).length;
  };

  // Helper funktion til at normalisere match ID (ekstraher nummer)
  const normalizeMatchId = (id) => {
    if (!id) return '';
    const match = id.toString().match(/(\d+)/);
    return match ? match[1] : '';
  };

  // Tjek om der er mismatch mellem gemte gæt og nuværende matches
  // Mismatch hvis det gemte gæt ikke er et af de nuværende hold i matchen
  // Brug savedPlayoffPredictions i stedet for playoffPredictions for at tjekke mod gemte værdier
  const hasPlayoffMismatch = () => {
    if (!savedGuesses.phase2Playoff || !playoffMatchups.length) return false;
    
    // Tjek hver nuværende match
    return playoffMatchups.some(match => {
      const matchId = match.id;
      const normalizedMatchId = normalizeMatchId(matchId);
      
      // Find det gemte gæt for denne match (prøv forskellige ID formater)
      // Brug savedPlayoffPredictions i stedet for playoffPredictions
      let savedGuess = '';
      for (const [key, value] of Object.entries(savedPlayoffPredictions)) {
        if (key === matchId || normalizeMatchId(key) === normalizedMatchId) {
          savedGuess = value;
          break;
        }
      }
      
      // Hvis der ikke er noget gæt for denne match, er der ikke mismatch (det er bare ikke gættet endnu)
      if (!savedGuess) return false;
      
      // Tjek om det gemte gæt er et af de nuværende hold i matchen
      const isHomeTeam = savedGuess === match.home;
      const isAwayTeam = savedGuess === match.away;
      
      // Hvis gættet ikke matcher nogen af de nuværende hold, er der mismatch
      return !isHomeTeam && !isAwayTeam;
    });
  };

  const hasRound16Mismatch = () => {
    if (!savedGuesses.phase2Round16 || !eighthFinalMatchups.length) return false;
    if (!savedKnockoutPredictions.eighthFinals) return true;
    
    // Tjek hver nuværende match
    return eighthFinalMatchups.some(match => {
      const matchId = match.id;
      const normalizedMatchId = normalizeMatchId(matchId);
      
      // Find det gemte gæt for denne match - brug savedKnockoutPredictions
      let savedGuess = '';
      for (const [key, value] of Object.entries(savedKnockoutPredictions.eighthFinals || {})) {
        if (key === matchId || normalizeMatchId(key) === normalizedMatchId) {
          savedGuess = value;
          break;
        }
      }
      
      if (!savedGuess) return false;
      
      // Tjek om det gemte gæt er et af de nuværende hold
      const isHomeTeam = savedGuess === match.home;
      const isAwayTeam = savedGuess === match.away;
      
      return !isHomeTeam && !isAwayTeam;
    });
  };

  const hasQuarterMismatch = () => {
    if (!savedGuesses.phase2Quarters || !quarterFinalMatchups.length) return false;
    if (!savedKnockoutPredictions.quarterFinals) return true;
    
    // Tjek hver nuværende match
    return quarterFinalMatchups.some(match => {
      const matchId = match.id;
      const normalizedMatchId = normalizeMatchId(matchId);
      
      // Find det gemte gæt for denne match - brug savedKnockoutPredictions
      let savedGuess = '';
      for (const [key, value] of Object.entries(savedKnockoutPredictions.quarterFinals || {})) {
        if (key === matchId || normalizeMatchId(key) === normalizedMatchId) {
          savedGuess = value;
          break;
        }
      }
      
      if (!savedGuess) return false;
      
      // Tjek om det gemte gæt er et af de nuværende hold
      const isHomeTeam = savedGuess === match.home;
      const isAwayTeam = savedGuess === match.away;
      
      return !isHomeTeam && !isAwayTeam;
    });
  };

  const hasSemiMismatch = () => {
    if (!savedGuesses.phase2Semis || !semiFinalMatchups.length) return false;
    if (!savedKnockoutPredictions.semiFinals) return true;
    
    // Tjek hver nuværende match
    return semiFinalMatchups.some(match => {
      const matchId = match.id;
      const normalizedMatchId = normalizeMatchId(matchId);
      
      // Find det gemte gæt for denne match - brug savedKnockoutPredictions
      let savedGuess = '';
      for (const [key, value] of Object.entries(savedKnockoutPredictions.semiFinals || {})) {
        if (key === matchId || normalizeMatchId(key) === normalizedMatchId) {
          savedGuess = value;
          break;
        }
      }
      
      if (!savedGuess) return false;
      
      // Tjek om det gemte gæt er et af de nuværende hold
      const isHomeTeam = savedGuess === match.home;
      const isAwayTeam = savedGuess === match.away;
      
      return !isHomeTeam && !isAwayTeam;
    });
  };

  // Find den første ikke-disabled tab som default
  const getDefaultActiveTab = () => {
    if (!isPlayoffLocked) return 0;
    if (!isRound16Locked) return 1;
    if (!isQuartersLocked) return 2;
    if (!isSemisLocked) return 3;
    return 0; // Fallback til første tab hvis alle er låst
  };

  return (
    <div className="phase-container">
      <h3>Fase 2 - Knockout</h3>
      <p>Playoff og knockout kampe:</p>
      
      <Tabs defaultActiveTab={getDefaultActiveTab()} className="phase2-tabs">
        {/* Play-off Round */}
        <TabPanel tabId={0} label={isPlayoffLocked ? "🔒 Play-off" : "Play-off"} disabled={isPlayoffLocked}>
          <SaveStatus 
            isSaved={savedGuesses.phase2Playoff}
            hasMismatch={hasPlayoffMismatch()}
            roundName="Play-off"
          />
          <div className="tournament-bracket">
            <div className="bracket-round">
              <RoundHeader 
                title="PLAY-OFF" 
                pointValue={POINT_SYSTEM.PLAYOFF_MATCH} 
                description="point for korrekt samlet vinder"
              />
              <div className="bracket-matches">
                {playoffMatchups.map((match, index) => (
                  <div key={match.id || index} className="bracket-match">
                    <MatchComponent
                      team1={match.home}
                      team2={match.away}
                      matchId={match.id}
                      selectedWinner={playoffPredictions[match.id]}
                      onWinnerSelect={(winner) => onPlayoffPredictionChange(match.id, winner)}
                      disabled={(savedGuesses.phase2Playoff && !hasPlayoffMismatch()) || isPlayoffLocked}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SaveGuessSection
            isSaved={savedGuesses.phase2Playoff}
            hasMismatch={hasPlayoffMismatch()}
            areAllGuessed={areAllPlayoffMatchesGuessed()}
            guessedCount={getPlayoffGuessedCount()}
            totalMatches={KNOCKOUT_MATCHES.PLAYOFF}
            onSave={() => onSaveGroupGuess('Playoff')}
            isLocked={isPlayoffLocked}
            roundName="Playoff"
            roundNamePlural="playoff"
          />
        </TabPanel>

        {/* 1/8 Finals Round */}
        <TabPanel tabId={1} label={isRound16Locked ? "🔒 1/8-finale" : "1/8-finale"} disabled={isRound16Locked}>
          <SaveStatus 
            isSaved={savedGuesses.phase2Round16}
            hasMismatch={hasRound16Mismatch()}
            roundName="1/8-finale"
          />
          <div className="tournament-bracket">
            <div className="bracket-round">
              <RoundHeader 
                title="1/8-finale" 
                pointValue={POINT_SYSTEM.EIGHTH_FINAL_MATCH} 
              />
              <div className="bracket-matches">
                {eighthFinalMatchups.map((match, index) => (
                  <div key={match.id || index} className="bracket-match">
                    <MatchComponent
                      team1={match.home}
                      team2={match.away}
                      matchId={match.id}
                      selectedWinner={knockoutPredictions.eighthFinals?.[match.id]}
                      onWinnerSelect={(winner) => onKnockoutPredictionChange('eighthFinals', match.id, winner)}
                      disabled={(savedGuesses.phase2Round16 && !hasRound16Mismatch()) || isRound16Locked}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SaveGuessSection
            isSaved={savedGuesses.phase2Round16}
            hasMismatch={hasRound16Mismatch()}
            areAllGuessed={areAllRound16MatchesGuessed()}
            guessedCount={getRound16GuessedCount()}
            totalMatches={KNOCKOUT_MATCHES.EIGHTH_FINALS}
            onSave={() => onSaveGroupGuess('Round16')}
            isLocked={isRound16Locked}
            roundName="1/8-finale"
            roundNamePlural="1/8-finaler"
          />
        </TabPanel>

        {/* Quarter Finals Round */}
        <TabPanel tabId={2} label={isQuartersLocked ? "🔒 Kvartfinale" : "Kvartfinale"} disabled={isQuartersLocked}>
          <SaveStatus 
            isSaved={savedGuesses.phase2Quarters}
            hasMismatch={hasQuarterMismatch()}
            roundName="Kvartfinale"
          />
          <div className="tournament-bracket">
            <div className="bracket-round">
              <RoundHeader 
                title="KVARTFINALE" 
                pointValue={POINT_SYSTEM.QUARTER_FINAL_MATCH} 
              />
              <div className="bracket-matches">
                {quarterFinalMatchups.map((match, index) => (
                  <div key={match.id || index} className="bracket-match">
                    <MatchComponent
                      team1={match.home}
                      team2={match.away}
                      matchId={match.id}
                      selectedWinner={knockoutPredictions.quarterFinals?.[match.id]}
                      onWinnerSelect={(winner) => onKnockoutPredictionChange('quarterFinals', match.id, winner)}
                      disabled={(savedGuesses.phase2Quarters && !hasQuarterMismatch()) || isQuartersLocked}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SaveGuessSection
            isSaved={savedGuesses.phase2Quarters}
            hasMismatch={hasQuarterMismatch()}
            areAllGuessed={areAllQuarterMatchesGuessed()}
            guessedCount={getQuarterGuessedCount()}
            totalMatches={KNOCKOUT_MATCHES.QUARTER_FINALS}
            onSave={() => onSaveGroupGuess('Quarters')}
            isLocked={isQuartersLocked}
            roundName="Kvartfinale"
            roundNamePlural="Kvartfinaler"
          />
        </TabPanel>

        {/* Semi Finals Round */}
        <TabPanel tabId={3} label={isSemisLocked ? "🔒 Semifinale" : "Semifinale"} disabled={isSemisLocked}>
          <SaveStatus 
            isSaved={savedGuesses.phase2Semis}
            hasMismatch={hasSemiMismatch()}
            roundName="Semifinale"
          />
          <div className="tournament-bracket">
            <div className="bracket-round">
              <RoundHeader 
                title="SEMIFINALE" 
                pointValue={POINT_SYSTEM.SEMI_FINAL_MATCH} 
              />
              <div className="bracket-matches">
                {semiFinalMatchups.map((match, index) => (
                  <div key={match.id || index} className="bracket-match">
                    <MatchComponent
                      team1={match.home}
                      team2={match.away}
                      matchId={match.id}
                      selectedWinner={knockoutPredictions.semiFinals?.[match.id]}
                      onWinnerSelect={(winner) => onKnockoutPredictionChange('semiFinals', match.id, winner)}
                      disabled={(savedGuesses.phase2Semis && !hasSemiMismatch()) || isSemisLocked}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SaveGuessSection
            isSaved={savedGuesses.phase2Semis}
            hasMismatch={hasSemiMismatch()}
            areAllGuessed={areAllSemiMatchesGuessed()}
            guessedCount={getSemiGuessedCount()}
            totalMatches={KNOCKOUT_MATCHES.SEMI_FINALS}
            onSave={() => onSaveGroupGuess('Semis')}
            isLocked={isSemisLocked}
            roundName="Semifinale"
            roundNamePlural="Semifinaler"
          />
        </TabPanel>
      </Tabs>
      
      <PointSummary
        rules={[
          { text: `Gæt på det hold som du tror går videre` },
          { text: `${POINT_SYSTEM.PLAYOFF_MATCH} point for hvert korrekt gæt i Play-off` },
          { text: `${POINT_SYSTEM.EIGHTH_FINAL_MATCH} point for hvert korrekt gæt i 1/8-finale` },
          { text: `${POINT_SYSTEM.QUARTER_FINAL_MATCH} point for hvert korrekt gæt i Kvartfinale` },
          { text: `${POINT_SYSTEM.SEMI_FINAL_MATCH} point for hvert korrekt gæt i Semifinale` }
        ]}
      />
    </div>
  );
};

export default Phase2;
